// src/components/SpeechToTextRecorder.js
import React, { useEffect, useRef, useState } from 'react';

/**
 * SpeechToTextRecorder
 *
 * Props:
 *   - value:       the current text value (controlled by parent), or you can let this be internal
 *   - onChange:    callback(text) => void, called whenever new transcript is available
 *   - label:       label shown above textarea (optional)
 *   - placeholder: placeholder in the textarea (optional)
 *
 *   - uploadEndpoint:   URL to POST chunked audio (default: '/api/timesheets/upload-audio')
 *   - transcribeEndpoint: URL to GET final transcript  (default: '/api/timesheets/transcribe?sessionId=xxx')
 *   - cleanupEndpoint: URL to DELETE after unmount     (default: '/api/timesheets/cleanup?sessionId=xxx')
 *
 *   - required?:        boolean - if you want to require a value
 *   - className?:       you can pass additional styling, etc.
 *   - ...any other standard props
 *
 * This component handles:
 *   1. Microphone access, chunk-based recording
 *   2. Uploading each chunk to the backend
 *   3. Polling the new transcript
 *   4. Merging it into the text, calling onChange
 *   5. On unmount, cleaning up (server-side) the partial audio
 *
 */
const SpeechToTextRecorder = ({
  value,
  onChange,
  label = 'Speak Your Text',
  placeholder = 'Speak or type here...',
  uploadEndpoint = '/api/timesheets/upload-audio',
  transcribeEndpoint = '/api/timesheets/transcribe',
  cleanupEndpoint = '/api/timesheets/cleanup',
  required = false,
  className = '',
  ...props
}) => {
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const sessionIdRef = useRef(null);

  // For sequential chunk processing
  const transcribeQueueRef = useRef(Promise.resolve());

  /**
   * processChunk(blob, extension)
   * 1) Upload chunk to the server
   * 2) Transcribe (GET) from server
   * 3) Fire onChange with the updated transcript
   */
  const processChunk = async (blob, extension) => {
    try {
      // 1) Upload chunk
      const formData = new FormData();
      formData.append('sessionId', sessionIdRef.current);
      formData.append('audio', blob, `chunk-${Date.now()}.${extension}`);

      await fetch(uploadEndpoint, { method: 'POST', body: formData });

      // 2) Transcribe
      const resp = await fetch(`${transcribeEndpoint}?sessionId=${sessionIdRef.current}`);
      const data = await resp.json();

      // 3) Update parent with new transcript
      if (data.transcript && onChange) {
        onChange(data.transcript); // calls the parent's callback
      }
    } catch (err) {
      console.error('Error processing chunk:', err);
    }
  };

  /**
   * startRecording()
   * 1) getUserMedia
   * 2) Choose correct MIME type
   * 3) start chunked recording
   */
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

      let mimeType = 'audio/webm; codecs=opus';
      if (isIOS && MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/webm; codecs=opus')) {
        mimeType = 'audio/webm; codecs=opus';
      }
      const fileExtension = (isIOS && mimeType === 'audio/mp4') ? 'mp4' : 'webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      // Unique session ID
      sessionIdRef.current = Date.now().toString() + '_speech2txt';
      setIsRecording(true);

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          // Ensure sequential chunk processing
          transcribeQueueRef.current = transcribeQueueRef.current.then(() =>
            processChunk(e.data, fileExtension)
          );
        }
      };

      // For iOS, timeslice=1000ms; otherwise 4000ms
      const timeslice = isIOS ? 1000 : 4000;
      recorder.start(timeslice);
    } catch (err) {
      console.error('Error starting recording:', err);
      alert('Could not start recording. Check microphone permissions.');
    }
  };

  /**
   * stopRecording()
   * stops the recorder, stops the mic tracks
   */
  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
      mediaRecorderRef.current = null;
      setIsRecording(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sessionIdRef.current) {
        // optional server cleanup
        fetch(`${cleanupEndpoint}?sessionId=${sessionIdRef.current}`, {
          method: 'DELETE',
        }).catch((err) =>
          console.error('Cleanup error in SpeechToTextRecorder:', err)
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`flex flex-col ${className}`}>
      {label && (
        <label className="block mb-1 font-semibold">{label}</label>
      )}
      {/* Text area to display current transcript */}
      <textarea
        value={value || ''}
        onChange={(e) => onChange && onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full p-3 text-base border border-gray-300 rounded-md focus:border-[#1F5683] focus:outline-none min-h-[80px]"
        {...props}
      />
      <div className="mt-2">
        <button
          type="button"
          onClick={isRecording ? stopRecording : startRecording}
          className={`px-4 py-2 text-white rounded-md transition-colors ${
            isRecording
              ? 'bg-red-500 hover:bg-red-600'
              : 'bg-[#1F5683] hover:bg-[#246399]'
          }`}
        >
          {isRecording ? 'Stop Recording' : 'Start Recording'}
        </button>
      </div>
    </div>
  );
};

export default SpeechToTextRecorder;
